/**
 * EventDoorPage — /events/:slug/join
 *
 * The registration-event sibling of CallLandingPage's door gate (see
 * worker/src/event-door-routes.ts + sop-ee-event-attendance.md). Deliberately
 * minimal — this is a doorway, not a landing page. Fetches the door payload
 * from GET /api/public/events/:slug/door, resolves the visitor's identity from
 * a personal-link token (?at=) or device memory, and — inside the door
 * window — shows a one-tap "You're joining as Jane" confirm card. Tapping
 * records verified attendance (fire-and-forget) before opening the gated
 * join redirect. Anonymous visitors are asked for the email they registered
 * with (EventDoorEmailCard, Phase C) so verified attendance can still be
 * recorded without a token. After the event, the join slot swaps for a replay
 * CTA — never a dead join button.
 *
 * FAIL-OPEN: if the door payload fetch itself fails, render a bare join
 * anchor to /zoom/events/:slug — the server redirect works standalone and
 * enforces the real window regardless of what this page thinks the time is.
 *
 * Phase C adds the tokenless email path (EventDoorEmailCard below) for
 * anonymous visitors — mirrors CallLandingPage's DoorEmailCard exactly (kept
 * as a separate inline component rather than a shared extraction, so as not
 * to touch CallLandingPage ahead of its own first live test).
 *
 * Multi-day events (Summit 2026 — the door payload's optional `days` array,
 * CC/EE event unification Phase 2 Batch C) share this SAME page and SAME
 * join target across every day (one Komunily community-events page hosts
 * both days) — only the window/countdown clock differs: instead of ticking
 * against one static eventDate, `pickRelevantDay` finds whichever listed day
 * is currently live, or the next one still ahead, and the existing
 * before/live/after rendering runs unchanged against THAT day. A
 * single-occurrence event (`days` absent) is completely unaffected — it
 * falls through to the original single-eventDate clock.
 */

import { useEffect, useState, useRef, type FormEvent, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { suggestTypoCorrection } from './lib/email-validator';
import { googleCalendarLinks } from './lib/calendar';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const GET_HOST = 'https://get.thesuccessfulbookkeeper.com';

/** Minutes before eventDate at which the door opens — mirrors EVENT_DOOR_OPEN_MS
 *  in worker/src/event-door-routes.ts. */
const DOOR_OPEN_MINUTES = 15;
/** Grace window after the event's computed end at which the door closes for
 *  good — mirrors EVENT_GRACE_AFTER_END_MS. */
const GRACE_MINUTES_AFTER_END = 180;

type DoorIdentity = { firstName: string | null; email: string };

/** Door email card (attendance identity gate, Phase C). Shown when the doors
 *  are open and we don't already know the visitor (no personal-link token, no
 *  device memory). They enter the email they registered with; the engine
 *  resolves it (direct submissions match + HubSpot secondary-email). Recognized
 *  → hand back to the confirm card. Not recognized → a gentle retry, then
 *  "join anyway" as a guest (records + Slack-logs server-side). A quiet "just
 *  join" link always lets someone in without identifying — the gate records,
 *  it never blocks entry. */
function EventDoorEmailCard({ slug, joinUrl, joinBtnStyle, onRecognized }: {
  slug: string;
  joinUrl: string;
  joinBtnStyle: CSSProperties;
  onRecognized: (id: DoorIdentity) => void;
}) {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'entry' | 'retry'>('entry');
  const [busy, setBusy] = useState(false);
  const suggestion = suggestTypoCorrection(email);

  const openZoom = () => window.open(joinUrl, '_blank', 'noopener,noreferrer');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean.includes('@')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/public/events/${encodeURIComponent(slug)}/checkin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean }),
      });
      const d = await r.json().catch(() => ({})) as { recognized?: boolean; firstName?: string | null };
      if (d.recognized) { onRecognized({ firstName: d.firstName ?? null, email: clean }); return; }
      setPhase('retry');
    } catch {
      // Fail-open: if resolution errors, let them straight in.
      openZoom();
    } finally {
      setBusy(false);
    }
  };

  // Guest admit — record + Slack-log server-side, then enter regardless.
  const joinAsGuest = () => {
    const clean = email.trim().toLowerCase();
    if (clean.includes('@')) {
      fetch(`${API_BASE}/api/public/events/${encodeURIComponent(slug)}/checkin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean, guest: true }), keepalive: true,
      }).catch(() => { /* the gate records; it never blocks entry */ });
    }
    openZoom();
  };

  const inputStyle: CSSProperties = {
    width: '100%', padding: '11px 13px', borderRadius: 8, fontSize: 14,
    border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)',
    color: '#fff', outline: 'none', boxSizing: 'border-box',
  };
  const linkStyle: CSSProperties = {
    background: 'none', border: 'none', color: '#9ca3af', fontSize: 12.5,
    cursor: 'pointer', textDecoration: 'underline', padding: 0,
  };

  return (
    <div style={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch', gap: 10,
      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: 12, padding: '18px 22px', maxWidth: 380, width: '100%', textAlign: 'left',
    }}>
      <div style={{ fontSize: 13.5, color: '#e5e7eb', textAlign: 'center', lineHeight: 1.5 }}>
        {phase === 'entry'
          ? 'So we know who’s joining, what’s the email you registered with?'
          : 'We don’t see that email — is there another one you might use?'}
      </div>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourfirm.com" autoComplete="email" style={inputStyle} required />
        {suggestion && (
          <button type="button" onClick={() => setEmail(suggestion)}
            style={{ ...linkStyle, textAlign: 'left', color: '#F8A07A' }}>
            Did you mean {suggestion}?
          </button>
        )}
        <button type="submit" disabled={busy}
          style={{ ...joinBtnStyle, border: 'none', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? '…' : phase === 'entry' ? 'Continue' : 'Try this email'}
        </button>
      </form>
      {phase === 'retry' && (
        <button type="button" onClick={joinAsGuest} style={{ ...linkStyle, textAlign: 'center' }}>
          Join anyway
        </button>
      )}
      <button type="button" onClick={openZoom} style={{ ...linkStyle, textAlign: 'center', color: '#6b7280' }}>
        Having trouble? Just join
      </button>
    </div>
  );
}

/** "Add to your calendar" (mca_06a359d8b6a0 / decision mcd_fa12ebfa-a0a). The
 *  Summit emails label this page's link "Add the Summit to your calendar", so
 *  the page has to actually do that. Google gets a prefilled link (one
 *  recurring entry when the days are a clean daily series, see
 *  lib/calendar.ts); Outlook and Apple get the worker's .ics
 *  (GET /api/public/events/:slug/calendar.ics), served from the branded
 *  worker host with text/calendar so iPhone Safari offers "Add to Calendar"
 *  natively. Every instant is UTC, so each calendar shows the visitor's own
 *  zone. The join link inside the entry is THIS page, with the visitor's
 *  personal ?at= token when they arrived with one — the worker only embeds a
 *  token that verifies for this slug. Shown before and during the event,
 *  never after it. */
function DoorCalendarCard({ slug, title, days, eventTimezone }: {
  slug: string;
  title: string;
  days: { label: string; date: string; durationMinutes: number }[];
  eventTimezone: string | null;
}) {
  const at = new URLSearchParams(window.location.search).get('at');
  const joinUrl = `${window.location.origin}/events/${encodeURIComponent(slug)}/join${at ? `?at=${encodeURIComponent(at)}` : ''}`;
  const details = `Your join link: ${joinUrl}\n\n${days.length > 1 ? 'Open it on each day' : 'Open it on the day'} \u2014 the doors open ${DOOR_OPEN_MINUTES} minutes before the start.`;
  const google = googleCalendarLinks({ title, days, details, location: joinUrl });
  if (!google.length) return null;
  const icsHref = `${GET_HOST}/api/public/events/${encodeURIComponent(slug)}/calendar.ics${at ? `?at=${encodeURIComponent(at)}` : ''}`;

  // The page header shows the event's own zone. If the visitor's clock reads
  // differently, list each day in their zone too.
  const localLines = (() => {
    if (!eventTimezone) return [];
    try {
      const probe: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
      const first = new Date(days[0].date);
      const mine = new Intl.DateTimeFormat('en-US', probe).format(first);
      const theirs = new Intl.DateTimeFormat('en-US', { ...probe, timeZone: eventTimezone }).format(first);
      if (mine === theirs) return [];
      const fmt = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
      return days.map((d) => ({ label: d.label, text: fmt.format(new Date(d.date)) }));
    } catch { return []; }
  })();

  const btn: CSSProperties = {
    display: 'inline-block', padding: '10px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
    textDecoration: 'none', color: '#fff', background: 'rgba(255,255,255,0.10)',
    border: '1px solid rgba(255,255,255,0.22)', whiteSpace: 'nowrap',
  };

  return (
    <div data-testid="door-calendar" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 12, padding: '18px 22px', maxWidth: 460, width: '100%', boxSizing: 'border-box',
    }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Add it to your calendar</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
        {google.map((g) => (
          <a key={g.href} href={g.href} target="_blank" rel="noopener noreferrer" style={btn}>
            {g.label ? `Google Calendar \u00b7 ${g.label}` : 'Google Calendar'}
          </a>
        ))}
        <a href={icsHref} style={btn}>Outlook</a>
        <a href={icsHref} style={btn}>Apple Calendar</a>
      </div>
      <div style={{ fontSize: 12.5, color: '#9ca3af', textAlign: 'center', lineHeight: 1.5 }}>
        {days.length > 1 ? `Adds all ${days.length} days, with your join link inside.` : 'Adds it with your join link inside.'}
      </div>
      {localLines.length > 0 && (
        <div style={{ fontSize: 12.5, color: '#cbd5e1', textAlign: 'center', lineHeight: 1.6 }}>
          <div style={{ color: '#9ca3af' }}>In your time zone:</div>
          {localLines.map((l) => (
            <div key={l.label}>{localLines.length > 1 ? `${l.label}: ` : ''}{l.text}</div>
          ))}
        </div>
      )}
    </div>
  );
}

interface DoorWhen {
  dayLong: string;
  timeShort: string;
  dateTimeFull: string;
}

interface DoorDay {
  key: string;
  label: string;
  date: string;
  durationMinutes: number | null;
  when: DoorWhen;
}

interface DoorPayload {
  found: boolean;
  title?: string;
  when?: DoorWhen | null;
  eventDate?: string | null;
  durationMinutes?: number | null;
  doorGate?: boolean;
  zoom_ready?: boolean;
  doors_open?: boolean;
  replay?: { status: 'live' | null };
  /** Present only for multi-day events (Summit). Every named day, each with
   *  its own formatted `when`. Absent = single-occurrence event, unchanged
   *  clock behavior. */
  days?: DoorDay[];
  /** IANA zone of the event record (e.g. Australia/Sydney). */
  eventTimezone?: string | null;
  /** The event's own logo, from its registration page's top bar. null = TSB. */
  logo?: { url: string; alt: string; href: string | null } | null;
  /** What the event calls itself — "demo". Absent = "webinar". */
  noun?: string;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'fetch-error' }
  | { phase: 'not-found' }
  | { phase: 'ready'; door: DoorPayload };

/** Where the visitor is relative to the door window, computed client-side off
 *  the raw eventDate/durationMinutes so the countdown can tick every second.
 *  The server is the real authority — /zoom/events/:slug re-checks the window
 *  on every click regardless of what this clock thinks. */
type WindowPhase = 'before' | 'live' | 'after';

function windowPhase(eventDateIso: string | null | undefined, durationMinutes: number | null | undefined): WindowPhase {
  if (!eventDateIso) return 'before';
  const startMs = new Date(eventDateIso).getTime();
  if (!Number.isFinite(startMs)) return 'before';
  const doorOpenMs = startMs - DOOR_OPEN_MINUTES * 60_000;
  const endMs = startMs + (durationMinutes && durationMinutes > 0 ? durationMinutes : 60) * 60_000;
  const graceEndMs = endMs + GRACE_MINUTES_AFTER_END * 60_000;
  const now = Date.now();
  if (now < doorOpenMs) return 'before';
  if (now > graceEndMs) return 'after';
  return 'live';
}

/** For a multi-day door payload, pick which listed day is currently relevant
 *  to the clock: the one that's live right now, else the next one still
 *  ahead, else (all days over) the last one — so the existing before/live/
 *  after rendering below always has a concrete day to tick against, exactly
 *  as if it were the only day. Returns null when `days` is absent/empty —
 *  the caller falls back to the legacy single-eventDate fields, unchanged. */
function pickRelevantDay(days: DoorDay[] | undefined): DoorDay | null {
  if (!days?.length) return null;
  const live = days.find((d) => windowPhase(d.date, d.durationMinutes) === 'live');
  if (live) return live;
  const upcoming = days.find((d) => windowPhase(d.date, d.durationMinutes) === 'before');
  if (upcoming) return upcoming;
  return days[days.length - 1];
}

function secondsUntilDoorOpen(eventDateIso: string): number {
  const doorMs = new Date(eventDateIso).getTime() - DOOR_OPEN_MINUTES * 60_000;
  return Math.floor((doorMs - Date.now()) / 1000);
}

function formatCountdown(secs: number): string {
  if (secs <= 0) return '';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (d > 0) return `${d}d ${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Ticks every second, recomputing both the countdown and the window phase so
 *  the page transitions from countdown → live → after without a reload. */
function useDoorClock(eventDateIso: string | null | undefined, durationMinutes: number | null | undefined) {
  const [tick, setTick] = useState(0);
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!eventDateIso) return;
    ref.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [eventDateIso]);

  const phase = windowPhase(eventDateIso, durationMinutes);
  const countdownSecs = eventDateIso ? secondsUntilDoorOpen(eventDateIso) : -1;
  return { phase, countdownSecs, tick };
}

export default function EventDoorPage() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [identity, setIdentity] = useState<DoorIdentity | null>(null);
  const [notMe, setNotMe] = useState(false);

  // The email card resolved a registrant — greet them (confirm card) + remember
  // the device. The email-match attendance row was already recorded server-side.
  const handleRecognized = (id: DoorIdentity) => {
    setNotMe(false);
    setIdentity(id);
    try { localStorage.setItem(`event-checkin-id:${slug}`, JSON.stringify(id)); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!slug) { setState({ phase: 'fetch-error' }); return; }
    fetch(`${API_BASE}/api/public/events/${encodeURIComponent(slug)}/door`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`Server error ${r.status}`);
        const d = await r.json() as DoorPayload;
        setState(d.found ? { phase: 'ready', door: d } : { phase: 'not-found' });
      })
      .catch(() => setState({ phase: 'fetch-error' }));
  }, [slug]);

  // Resolve identity: device memory first (returning visitor, same device),
  // then the personal-link token (?at=) if present. Both fail-open silently —
  // an unresolved identity just means the anonymous join button shows instead.
  useEffect(() => {
    if (!slug) return;
    try {
      const remembered = localStorage.getItem(`event-checkin-id:${slug}`);
      if (remembered) setIdentity(JSON.parse(remembered));
    } catch { /* ignore */ }
    const at = new URLSearchParams(window.location.search).get('at');
    if (!at) return;
    fetch(`${API_BASE}/api/public/events/${encodeURIComponent(slug)}/identity?at=${encodeURIComponent(at)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.identity) setIdentity(d.identity); })
      .catch(() => { /* fail-open: stay anonymous */ });
  }, [slug]);

  // Fire-and-forget: record the door check-in. Never awaited — the Zoom tab
  // opens natively via the anchor regardless of whether this call succeeds.
  const recordCheckin = () => {
    if (!slug) return;
    try { localStorage.setItem(`event-checkin-id:${slug}`, JSON.stringify(identity)); } catch { /* ignore */ }
    const at = new URLSearchParams(window.location.search).get('at');
    if (!at) return; // no token → greeting only, attendance falls back to Zoom-report matching
    fetch(`${API_BASE}/api/public/events/${encodeURIComponent(slug)}/checkin`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ at }), keepalive: true,
    }).catch(() => { /* the gate records; it never blocks entry */ });
  };

  const door = state.phase === 'ready' ? state.door : null;
  // Multi-day (Summit): tick the clock against whichever listed day is
  // currently live or next up, instead of a single static eventDate. Absent
  // `days` (every other registration event) falls straight through to the
  // legacy single-value fields, byte-identical to before this change.
  const relevantDay = pickRelevantDay(door?.days);
  const referenceDate = relevantDay?.date ?? door?.eventDate ?? null;
  const referenceDuration = relevantDay?.durationMinutes ?? door?.durationMinutes ?? null;
  const { phase: windowState, countdownSecs } = useDoorClock(referenceDate, referenceDuration);
  const joinUrl = slug ? `${GET_HOST}/zoom/events/${encodeURIComponent(slug)}` : null;

  // Hard fail-open: the door payload itself couldn't be fetched. We don't know
  // the window, but the server-side redirect enforces it regardless — a bare
  // anchor here still works standalone.
  if (state.phase === 'fetch-error') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center' }}>
          <div style={{ color: '#6b7280', fontSize: 14 }}>Having trouble loading this page.</div>
          {joinUrl && (
            <a href={joinUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', background: '#F05A28', color: '#fff', padding: '14px 32px', borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
              Join the webinar
            </a>
          )}
        </div>
      </TakerShell>
    );
  }

  if (state.phase === 'loading') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>
        </div>
      </TakerShell>
    );
  }

  if (state.phase === 'not-found') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: '#991b1b', fontSize: 15 }}>This event was not found.</div>
        </div>
      </TakerShell>
    );
  }

  const noun = door?.noun || 'webinar';
  const title = door?.title ?? `Your ${noun}`;
  const when = relevantDay?.when ?? door?.when ?? null;
  const replayLive = door?.replay?.status === 'live';

  const joinBtnStyle: CSSProperties = {
    display: 'inline-block', background: '#F05A28', color: '#fff',
    padding: '14px 36px', borderRadius: 8, fontSize: 15, fontWeight: 700,
    textDecoration: 'none', letterSpacing: 0.2,
  };
  const cardStyle: CSSProperties = {
    display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 12, padding: '18px 28px', maxWidth: 380,
  };

  const bodySlot = (() => {
    if (windowState === 'after') {
      if (replayLive) {
        return (
          <div style={cardStyle}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#9ca3af' }}>
              This {noun} has wrapped
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', textAlign: 'center' }}>
              The replay is ready to watch.
            </div>
            <a href={`/replays/${encodeURIComponent(slug ?? '')}`} className="cta-btn" style={joinBtnStyle}>
              Watch the replay
            </a>
          </div>
        );
      }
      return (
        <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', maxWidth: 380 }}>
          This event has already taken place. Keep an eye on your inbox — the replay is on its way.
        </div>
      );
    }

    if (windowState === 'before') {
      return (
        <div style={{
          display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: 12, padding: '16px 32px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#9ca3af' }}>
            Doors open in
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#F8A07A', fontVariantNumeric: 'tabular-nums', letterSpacing: 0.5 }}>
            {formatCountdown(countdownSecs)}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {DOOR_OPEN_MINUTES} minutes before the {noun} starts
          </div>
        </div>
      );
    }

    // windowState === 'live'
    if (!door?.zoom_ready || !joinUrl) {
      return (
        <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center' }}>
          The join link hasn't been set up yet — check your reminder email, or check back shortly.
        </div>
      );
    }

    if (identity && !notMe) {
      const who = identity.firstName?.trim() || identity.email;
      return (
        <div style={cardStyle}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#9ca3af' }}>
            You're joining as
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{who}</div>
          <a href={joinUrl} target="_blank" rel="noopener noreferrer"
            className="cta-btn" onClick={recordCheckin} style={joinBtnStyle}>
            Yes — enter the {noun}
          </a>
          <button type="button" onClick={() => setNotMe(true)}
            style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
            Not you?
          </button>
        </div>
      );
    }

    // Anonymous visitor (no token, no device memory, or "Not you?") — ask for
    // the registered email and resolve it (Phase C). "Just join" preserves the
    // anonymous path, so nobody is ever blocked.
    return (
      <EventDoorEmailCard
        slug={slug ?? ''}
        joinUrl={joinUrl}
        joinBtnStyle={joinBtnStyle}
        onRecognized={handleRecognized}
      />
    );
  })();

  return (
    <TakerShell
      ctaHref={windowState === 'live' ? joinUrl : null}
      ctaLabel={`Join the ${noun.charAt(0).toUpperCase()}${noun.slice(1)}`}
      logoUrl={door?.logo?.url ?? null}
      logoAlt={door?.logo?.alt}
      logoHref={door?.logo?.href ?? null}
      footerLabel="Registration event"
    >
      <section style={{
        background: 'linear-gradient(160deg, #0A0A2E 0%, #14143d 100%)',
        color: '#fff', padding: '72px 32px 88px', textAlign: 'center',
        fontFamily: "'Poppins', sans-serif", minHeight: '70vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 28,
      }}>
        <div>
          <h1 style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 'clamp(28px, 4.5vw, 40px)', fontWeight: 900, lineHeight: 1.15,
            marginBottom: 12, maxWidth: 640, margin: '0 auto 12px',
          }}>
            {title}
          </h1>
          {when && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 10, padding: '10px 20px', fontSize: 14, color: '#e2e8f0',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: windowState === 'after' ? '#6b7280' : '#4ade80', flexShrink: 0, boxShadow: windowState === 'after' ? 'none' : '0 0 6px #4ade80' }} />
              <span>{when.dateTimeFull}</span>
            </div>
          )}
        </div>
        {bodySlot}
        {windowState !== 'after' && slug && (
          <DoorCalendarCard
            slug={slug}
            title={title}
            days={door?.days?.length
              ? door.days.map((d) => ({ label: d.label, date: d.date, durationMinutes: d.durationMinutes ?? door.durationMinutes ?? 90 }))
              : door?.eventDate
                ? [{ label: 'Event', date: door.eventDate, durationMinutes: door.durationMinutes ?? 90 }]
                : []}
            eventTimezone={door?.eventTimezone ?? null}
          />
        )}
      </section>
    </TakerShell>
  );
}
